/**
 * @fileoverview Workspace Mail page (MOR-529).
 *
 * URL: /dashboard/mail — the SAME surface as the per-job Mail page (folder
 * switcher, search, thread reader, draft review), scoped across every active
 * mailbox on the user's jobs. Rows carry a job badge; selecting one loads
 * that job's thread or draft in the reading pane. The gear opens workspace
 * mail settings (global notification defaults + org sending domain); Compose
 * and Chat stay on the per-job page since they are mailbox-specific.
 */

import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { Mail, Settings } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ContactDetailPanel } from "@/components/jobs/phases/contacts/ContactDetailPanel";
import { Button } from "@/ui/primitives/button";
import { useActiveOrg } from "@/utils/org-context";
import { ComposeReader } from "./ComposeReader";
import { DraftReader } from "./DraftReader";
import { MailListPane, type MailFolder } from "./ThreadList";
import { ThreadView } from "./ThreadView";
import { WorkspaceMailSettings } from "./WorkspaceMailSettings";
import type { UserDraftListItem } from "./types";

const MAIL_ROUTE = "/_app/_auth/dashboard/_layout/mail" as const;

export function AggregateMailPage() {
  const { orgId } = useActiveOrg();
  const navigate = useNavigate();
  // Settings visibility is URL-driven (see route `validateSearch`) so the
  // sidebar "Mail" link reliably returns to the inbox.
  const { settings } = useSearch({ from: MAIL_ROUTE });
  const settingsOpen = settings === true;
  const toggleSettings = () => {
    void navigate({
      to: "/dashboard/mail",
      search: { settings: settingsOpen ? undefined : true },
    });
  };
  const [folder, setFolder] = useState<MailFolder>("all");
  const [selectedThread, setSelectedThread] = useState<{
    threadId: Id<"mailThreads">;
    jobId: Id<"jobs">;
  } | null>(null);
  const [selectedDraft, setSelectedDraft] = useState<{
    draftId: Id<"mailDrafts">;
    jobId: Id<"jobs">;
  } | null>(null);
  const [viewingContact, setViewingContact] = useState<{
    contactId: Id<"jobContacts">;
    jobId: Id<"jobs">;
  } | null>(null);

  const { data: pendingDraftCount } = useQuery(
    convexQuery(api.mail.drafts.workspacePendingDraftCount, {}),
  );
  const { data: unreadCount } = useQuery(
    convexQuery(api.mail.queries.workspaceUnreadCount, {}),
  );
  const { data: userDraftCount } = useQuery(
    convexQuery(api.mail.userDrafts.workspaceUserDraftCount, {}),
  );

  // A user compose draft opened in place in the reading pane (MOR-783) —
  // same in-place behaviour as threads and For Review drafts.
  const [openUserDraft, setOpenUserDraft] = useState<UserDraftListItem | null>(
    null,
  );

  // Contact rows for the full contact panel (merge picker needs them).
  const { data: contactJobRows } = useQuery(
    convexQueryOrIdle(
      api.contacts.queries.listJobContacts,
      viewingContact ? { jobId: viewingContact.jobId } : undefined,
    ),
  );

  const handleFolderChange = (next: MailFolder) => {
    setFolder(next);
    if (next !== "drafts") setSelectedDraft(null);
    if (next !== "userDrafts") setOpenUserDraft(null);
  };

  // User drafts open in place, like threads and For Review drafts: a reply
  // draft opens its thread (the reply composer resumes it); a compose draft
  // opens the composer in the reading pane.
  const handleSelectUserDraft = (draft: UserDraftListItem) => {
    if (draft.threadId) {
      setOpenUserDraft(null);
      setSelectedThread({ threadId: draft.threadId, jobId: draft.jobId });
    } else {
      setSelectedThread(null);
      setOpenUserDraft(draft);
    }
  };

  const showDraftReader = folder === "drafts";

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-0 flex-col overflow-hidden bg-muted">
      <div className="flex items-center justify-between gap-3 border-b bg-white px-6 py-4 dark:bg-card">
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-full bg-primary/10">
            <Mail className="size-4 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-primary">Mail</h1>
            <p className="text-sm text-muted-foreground">
              Manage all your mail across jobs. Go to your job inbox to create
              new emails.
            </p>
          </div>
        </div>
        <Button
          size="icon"
          variant={settingsOpen ? "secondary" : "ghost"}
          className="size-8"
          onClick={toggleSettings}
          title="Workspace mail settings"
          aria-label="Workspace mail settings"
        >
          <Settings className="size-4" />
        </Button>
      </div>

      {settingsOpen && orgId ? (
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <WorkspaceMailSettings orgId={orgId} />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-4 p-4">
          <div className="flex w-[400px] shrink-0 flex-col overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-card">
            <MailListPane
              folder={folder}
              onFolderChange={handleFolderChange}
              selectedThreadId={selectedThread?.threadId ?? null}
              onSelectThread={(threadId, jobId) => {
                setOpenUserDraft(null);
                setSelectedThread({ threadId, jobId });
              }}
              selectedDraftId={selectedDraft?.draftId ?? null}
              onSelectDraft={(draftId, jobId) => {
                setOpenUserDraft(null);
                setSelectedDraft({ draftId, jobId });
              }}
              selectedUserDraftId={openUserDraft?._id ?? null}
              onSelectUserDraft={handleSelectUserDraft}
              pendingDraftCount={pendingDraftCount ?? 0}
              unreadCount={unreadCount ?? 0}
              userDraftCount={userDraftCount ?? 0}
            />
          </div>
          <div className="min-w-0 flex-1 overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-card">
            {openUserDraft ? (
              <ComposeReader
                key={openUserDraft._id}
                jobId={openUserDraft.jobId}
                existingDraft={openUserDraft}
                mailboxEmail={openUserDraft.mailboxEmail ?? ""}
                onClose={() => setOpenUserDraft(null)}
              />
            ) : showDraftReader ? (
              selectedDraft ? (
                <DraftReader
                  jobId={selectedDraft.jobId}
                  draftId={selectedDraft.draftId}
                  onOpenContact={(contactId) =>
                    setViewingContact({ contactId, jobId: selectedDraft.jobId })
                  }
                />
              ) : (
                <ReaderEmptyState
                  title="No draft selected"
                  message="Choose a draft from the list to review it here."
                />
              )
            ) : selectedThread ? (
              <ThreadView
                jobId={selectedThread.jobId}
                threadId={selectedThread.threadId}
                onOpenContact={(contactId) =>
                  setViewingContact({ contactId, jobId: selectedThread.jobId })
                }
              />
            ) : (
              <ReaderEmptyState
                title="Nothing selected"
                message="Choose a thread from the list to read it here."
              />
            )}
          </div>
        </div>
      )}

      <ContactDetailPanel
        jobId={(viewingContact?.jobId ?? "") as Id<"jobs">}
        contactId={viewingContact?.contactId ?? null}
        open={viewingContact !== null}
        onClose={() => setViewingContact(null)}
        jobContacts={contactJobRows ?? []}
      />
    </div>
  );
}

function ReaderEmptyState({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-muted">
        <Mail className="size-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground">{message}</p>
    </div>
  );
}
